<script setup lang="ts">
import { EButton, EChangesSince, EDate, EPanel, ETabs, type TabDefinition } from '@erpx/ui'
import { useQuery } from '@tanstack/vue-query'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { RouterLink } from 'vue-router'

import AppIcon from '@/components/AppIcon.vue'
import RecordTimeline from '@/components/RecordTimeline.vue'
import { useRightRail } from '@/composables/useRightRail'
import { accessTokenExpiry } from '@/api/client'
import { rawT } from '@/i18n'
import { PERMISSIONS } from '@/lib/permissions'
import { QUERY_KEYS } from '@/lib/queryKeys'
import AssistantPanel from '@/modules/ai/AssistantPanel.vue'
import { AI_PERMISSIONS } from '@/modules/ai/permissions'
import { auditTimeline } from '@/modules/audit/api'
import AuditTrail from '@/modules/audit/AuditTrail.vue'
import { useAuthStore } from '@/stores/auth'

/**
 * The right slide-over panel (§11).
 *
 * It carries two faces. The **contextual** one — the record's own timestamps,
 * its **audit trail** read from `GET /audit/timeline` when the screen published
 * an entity to look up, the documents linked to it, and the session facts an
 * approver needs — and, since C12, the **AI Assistant** (§12), which lives here
 * rather than on a route of its own because an assistant you navigate away to
 * reach has lost the context it was going to answer about.
 *
 * **What changed since I last acted** (XFN-UX-015) sits above the full audit
 * trail on that same tab, for every document screen that publishes an entity —
 * one wiring here reaches all of them, rather than each document repeating it.
 * It reads the same `/audit/timeline` window `AuditTrail` already fetches (the
 * query key is shared, so this is not a second request) and derives the
 * baseline from the viewer's own last recorded act, never a locally stored
 * "last seen" marker (`ui/lib/changes.ts` explains why).
 *
 * Both the trail and the Assistant are permission-gated, for the same reason
 * the nav is: a user who cannot read the audit log is shown no trail rather
 * than an empty one implying nothing ever happened, and a user without
 * `ai.assistant.use` is shown **no Assistant tab at all**. When AI is disabled
 * at the instance `ai.Routes()` mounts nothing, so nobody holds that code and
 * the tab simply is not there — C12 §10.1's "hide, not error", by the ordinary
 * mechanism rather than a flag this component would have to be told about.
 */

const rail = useRightRail()
const auth = useAuthStore()
const { t } = useI18n()

const assistantAllowed = computed(() => auth.can(AI_PERMISSIONS.assistantUse))

const auditable = computed(() => {
  const record = rail.record.value
  if (record === null) return null
  if (record.entity === undefined || record.entity === '') return null
  if (record.entityId === undefined || record.entityId === '') return null
  if (!auth.can(PERMISSIONS.auditRead)) return null
  return { entity: record.entity, entityId: record.entityId }
})

/**
 * Same query `AuditTrail` runs for the same record — same key, so TanStack
 * Query serves one network call and both components read the same cache
 * entry. Kept here rather than lifted out of `AuditTrail` because that
 * component's whole point is "give it an entity and an id" with no other
 * coupling; duplicating the read is cheaper than threading a prop through it.
 */
const { data: changeEntries } = useQuery({
  queryKey: computed(() => QUERY_KEYS.auditTimeline(auditable.value?.entity ?? '', auditable.value?.entityId ?? '')),
  queryFn: () => auditTimeline(auditable.value?.entity ?? '', auditable.value?.entityId ?? ''),
  enabled: computed(() => auditable.value !== null),
})

const viewerId = computed(() => (auth.user?.id === undefined ? undefined : String(auth.user.id)))

const tabs = computed<TabDefinition[]>(() => {
  const list: TabDefinition[] = [
    {
      value: 'activity',
      label: t('shell.rail.tabs.activity'),
      count: rail.record.value?.events.length ?? 0,
    },
  ]
  if (auditable.value !== null) {
    list.push({ value: 'audit', label: t('shell.rail.tabs.audit') })
  }
  if (assistantAllowed.value) {
    // The label comes from the `ai` catalogue rather than `shell`, because
    // `web/src/locales/*.json` belongs to the wiring packet and a module
    // fragment is the only place this packet may add a string.
    list.push({ value: 'assistant', label: t('ai.assistant.tabLabel') })
  }
  list.push({ value: 'session', label: t('shell.rail.tabs.session') })
  return list
})

const tab = ref('activity')

// A record without a trail must not leave the rail parked on a tab that no
// longer exists.
watch(auditable, (value) => {
  if (value === null && tab.value === 'audit') tab.value = 'activity'
})

// `openAssistant()` sets the mode; the rail follows it onto the tab. A caller
// without the permission cannot get here — `useCommands` gates the command and
// the tab is absent — but the guard is repeated rather than assumed, because a
// rail parked on a tab that does not exist renders nothing at all.
watch(
  () => rail.mode.value,
  (value) => {
    if (value === 'assistant' && assistantAllowed.value) tab.value = 'assistant'
    if (value === 'context' && tab.value === 'assistant') tab.value = 'activity'
  },
  { immediate: true },
)

watch(assistantAllowed, (allowed) => {
  if (!allowed && tab.value === 'assistant') tab.value = 'activity'
})
</script>

<template>
  <aside v-if="rail.open.value" class="rail" :aria-label="$t('shell.rail.landmark')">
    <div class="rail__header">
      <h2 class="rail__title">{{ $t('shell.rail.title') }}</h2>
      <EButton
        variant="ghost"
        size="sm"
        icon-only
        :aria-label="$t('shell.rail.close')"
        @click="rail.toggle()"
      >
        <template #icon><AppIcon name="close" :size="15" /></template>
      </EButton>
    </div>

    <div class="rail__body">
      <ETabs v-model="tab" :tabs="tabs" variant="pill" :label="$t('shell.rail.sections')">
        <template #activity>
          <div class="stack">
            <div v-if="rail.record.value" class="stack--tight">
              <p class="timeline__label">{{ rail.record.value.title }}</p>
              <p v-if="rail.record.value.subtitle" class="timeline__meta">
                {{ rail.record.value.subtitle }}
              </p>
            </div>

            <RecordTimeline
              :events="rail.record.value?.events ?? []"
              :empty-title="$t('shell.rail.emptyTitle')"
              :empty-description="$t('shell.rail.emptyDescription')"
            />

            <div v-if="(rail.record.value?.links?.length ?? 0) > 0" class="stack--tight">
              <p class="rail__title">{{ $t('shell.rail.linked') }}</p>
              <RouterLink
                v-for="link in rail.record.value?.links ?? []"
                :key="link.to"
                class="link text-xs"
                :to="link.to"
              >
                {{ link.label }}
              </RouterLink>
            </div>
          </div>
        </template>

        <template v-if="auditable !== null" #audit>
          <div class="stack">
            <EChangesSince
              :entries="changeEntries ?? []"
              :options="{ viewerId, windowComplete: false }"
              :actor-name="(id: string) => $t('audit.trail.actorUser', { id })"
              :heading-label="rawT('shell.rail.changesSince.heading')"
              :up-to-date-label="$t('shell.rail.changesSince.upToDate')"
              :never-acted-label="$t('shell.rail.changesSince.neverActed')"
              :unknown-baseline-label="$t('shell.rail.changesSince.unknownBaseline')"
              :more-label="rawT('shell.rail.changesSince.more')"
              :empty-value-label="$t('shell.rail.changesSince.emptyValue')"
              :changed-to-label="$t('shell.rail.changesSince.changedTo')"
              :label="$t('shell.rail.changesSince.label')"
            />
            <AuditTrail :entity="auditable.entity" :entity-id="auditable.entityId" />
          </div>
        </template>

        <template v-if="assistantAllowed" #assistant>
          <AssistantPanel />
        </template>

        <template #session>
          <EPanel :title="$t('shell.rail.signedInAs')" padding="sm">
            <dl class="rail__facts">
              <dt>{{ $t('shell.rail.facts.name') }}</dt>
              <dd>{{ auth.displayName }}</dd>
              <dt>{{ $t('shell.rail.facts.email') }}</dt>
              <dd>{{ auth.user?.email }}</dd>
              <dt>{{ $t('shell.rail.facts.workspace') }}</dt>
              <dd>{{ auth.slug }}</dd>
              <dt>{{ $t('shell.rail.facts.roles') }}</dt>
              <dd>
                {{
                  auth.roleCodes.length > 0
                    ? auth.roleCodes.join(', ')
                    : $t('common.labels.noneAssigned')
                }}
              </dd>
              <dt>{{ $t('shell.rail.facts.permissions') }}</dt>
              <dd>{{ $t('shell.rail.facts.permissionsGranted', { count: auth.permissions.size }) }}</dd>
              <dt>{{ $t('shell.rail.facts.tokenExpires') }}</dt>
              <dd>
                <EDate :value="accessTokenExpiry()" preset="dateTime" :time-zone="auth.timeZone" />
              </dd>
            </dl>
          </EPanel>
        </template>
      </ETabs>
    </div>
  </aside>
</template>

<style>
.rail__facts {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: var(--erpx-space-1) var(--erpx-space-3);
  margin: 0;
  font-size: var(--erpx-text-xs);
}

.rail__facts dt {
  color: var(--erpx-color-text-muted);
}

.rail__facts dd {
  margin: 0;
  color: var(--erpx-color-text-primary);
  overflow-wrap: anywhere;
}
</style>
